/* The user drawer (M11): today's row controls, moved, with A1 on top. */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Drawer } from '../ui';
import { CONFIRM_GUARD_MS } from '../shared/CancelTaskConfirm';
import {
    findNode, PERSPECTIVE_LABEL, ROLE_LABEL, signInPath, TYPE_LABEL, userGuard, userKind,
} from '../../utils/adminConsole';
import { ACCESS_LABEL } from '../../utils/adminAccess';
import apiService from '../../services/apiService';
import { presetSummary } from '../../utils/rolePresets';
import {
    AccessCell, Alert, AppSwitch, Avatar, KindChip, Note, RoleText, SourceChip, StatusChip,
} from './bits';
import {
    CheckIcon, KeyIcon, LockIcon, PencilIcon, XIcon,
} from './icons';

const ROLE_HINT = {
    user: 'Uses IronSight, no admin rights.',
    dealer_admin: 'Manages plain users in their organization.',
    super_admin: 'Fusable staff, administers every dealership.',
};

function Verdict({ summary }) {
    if (summary.level === 'edit') {
        return (
            <div className="ad2-verdict ad2-verdict--edit">
                <b><PencilIcon /> Edit in {summary.edit.map((d) => d.name).join(', ')}</b>
                <span>Member there, so IronSight lets them change things.</span>
                <div className="ad2-can">
                    <span className="ad2-yes"><CheckIcon />View inventory</span>
                    <span className="ad2-yes"><CheckIcon />Create tasks</span>
                    <span className="ad2-yes"><CheckIcon />Notes and activity</span>
                </div>
            </div>
        );
    }
    if (summary.level === 'ro') {
        return (
            <div className="ad2-verdict ad2-verdict--ro">
                <b><LockIcon /> Read-only in {summary.ro.length} {summary.ro.length === 1 ? 'dealership' : 'dealerships'}</b>
                <span>
                    They reach these dealerships as <b>Fusable staff</b>, not as a member. They can look and
                    diagnose, but <b>cannot create tasks</b>, notes or other changes.
                </span>
                <div className="ad2-can">
                    <span className="ad2-yes"><CheckIcon />View inventory</span>
                    <span className="ad2-no"><XIcon />Create tasks</span>
                    <span className="ad2-no"><XIcon />Notes and activity</span>
                </div>
            </div>
        );
    }
    return (
        <div className="ad2-verdict ad2-verdict--none">
            <b>No dealership access</b>
            <span>{summary.why}. They can sign in but see no dealership data.</span>
        </div>
    );
}
Verdict.propTypes = { summary: PropTypes.object.isRequired };

function UserDrawer({
    user, c, me, tenants, summary, onClose, onToast,
}) {
    const { isSuper, orgs } = c;
    const ctx = { me, isSuper };
    const busy = c.busyId === user.id;
    const kind = userKind(user, orgs);
    const home = findNode(orgs, user.org_id);
    const self = Boolean(me?.email) && user.email === me.email;
    const gMove = userGuard(user, 'move', ctx);
    const gRole = userGuard(user, 'role', ctx);
    const gStatus = userGuard(user, 'status', ctx);
    const gRemove = userGuard(user, 'remove', ctx);
    const gApp = userGuard(user, 'app', ctx);
    const gReset = userGuard(user, 'reset', ctx);
    const confirming = c.confirmId === user.id;
    const confirmedAt = useRef(0);
    const removeYesRef = useRef(null);
    const [lastAction, setLastAction] = useState(null);
    // Role presets of the member's dealership (docs/decisions/role-presets.md).
    const presetOrgId = home?.type === 'COG' ? home.id : null;
    const [presets, setPresets] = useState(null);
    useEffect(() => {
        if (!presetOrgId) { setPresets(null); return undefined; }
        let alive = true;
        Promise.resolve(apiService.getAdminPresets?.(presetOrgId))
            .then((data) => { if (alive) setPresets({ items: Array.isArray(data?.items) ? data.items : [], canEdit: Boolean(data?.can_edit) }); })
            .catch(() => { if (alive) setPresets({ items: [], canEdit: false, error: true }); });
        return () => { alive = false; };
    }, [presetOrgId]);
    const presetInfo = presetSummary(user);

    // A fresh drawer starts without a stale error from another row.
    useEffect(() => { c.setActionError(null); }, [user.id]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (confirming) { confirmedAt.current = Date.now(); removeYesRef.current?.focus(); }
    }, [confirming]);

    const patch = (payload, done) => {
        setLastAction(Object.keys(payload)[0]);
        c.patchUser(user, payload).then((ok) => { if (ok && done) onToast(done); });
    };

    // Today's role choices, plus the current role so the select never shows a wrong value (P-ADM-052).
    const roleChoices = c.roleChoices.includes(user.role) || !user.role ? c.roleChoices : [...c.roleChoices, user.role];
    const tenantOf = new Map((tenants || []).map((t) => [t.org_id, t]));
    const dealers = c.orgOptions.filter((o) => o.type === 'COG');
    const addr = signInPath(home);

    return (
        <Drawer
            open
            onClose={onClose}
            width={520}
            title={(
                <span className="ad2-dr-title">
                    <Avatar user={user} kind={kind} large />
                    <span>{user.display_name}{self && <span className="ad2-you">YOU</span>}</span>
                </span>
            )}
            subtitle={user.email}
        >
            <div className="ad2-dr-chips">
                <KindChip kind={kind} />
                <RoleText role={user.role} />
                <StatusChip status={user.status} />
            </div>

            <Alert>{c.actionError}</Alert>

            {isSuper && summary && (
                <section className="ad2-dr-sec" aria-label="Dealership access">
                    <Verdict summary={summary} />
                    <h3>Dealership access <span className="ad2-prop">Expected</span></h3>
                    <div className="ad2-mx-wrap">
                        <table className="ad2-mx">
                            <thead><tr><th>Dealership</th><th>Access</th><th>Why</th></tr></thead>
                            <tbody>
                                {dealers.map((d) => (
                                    <tr key={d.id}>
                                        <td><div className="ad2-dn">{d.name}</div><SourceChip tenant={tenantOf.get(d.id)} /></td>
                                        <td><AccessCell level={summary.access[d.id]?.level} /></td>
                                        <td className="ad2-why">{summary.access[d.id]?.why}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <p className="ad2-foot-note">
                        Edit comes from membership. Read-only comes from the Fusable staff rule. There is no separate switch,
                        so a person can never be {ACCESS_LABEL.edit} somewhere they are not a member.
                    </p>
                </section>
            )}

            <section className="ad2-dr-sec" aria-label="Membership">
                <h3>Membership</h3>
                <div className="ad2-two">
                    <label className="ad2-fld">
                        <span>Home organization</span>
                        {user.org_id ? (
                            <select
                                className="v2-sel"
                                aria-label="Home organization"
                                value={user.org_id}
                                disabled={busy || !c.canEdit || Boolean(gMove)}
                                title="Move this user — this also sets what data they see"
                                onChange={(e) => patch({ org_id: e.target.value }, `${user.display_name} moved`)}
                            >
                                {c.orgOptions.map((o) => (
                                    <option key={o.id} value={o.id}>{'  '.repeat(o.depth)}{o.name} · {TYPE_LABEL[o.type]}</option>
                                ))}
                            </select>
                        ) : <span className="ad2-plain">{user.org_name || '—'}</span>}
                        {home && <span className="ad2-hint">Decides what they see: {(PERSPECTIVE_LABEL[home.type] || '').toLowerCase()}.</span>}
                    </label>
                    <label className="ad2-fld">
                        <span>Role</span>
                        <select
                            className="v2-sel"
                            aria-label="Role"
                            value={user.role || 'user'}
                            disabled={busy || !c.canEdit || Boolean(gRole)}
                            onChange={(e) => patch({ role: e.target.value }, `${user.display_name} is now ${ROLE_LABEL[e.target.value]}`)}
                        >
                            {roleChoices.map((r) => <option key={r} value={r}>{ROLE_LABEL[r] || r}</option>)}
                        </select>
                        <span className={`ad2-hint${gRole ? ' ad2-hint--warn' : ''}`}>{gRole || ROLE_HINT[user.role] || ''}</span>
                    </label>
                </div>
                {presetOrgId && (
                    <label className="ad2-fld ad2-dr-preset">
                        <span>Role preset</span>
                        <select
                            className="v2-sel"
                            aria-label="Role preset"
                            value={user.preset_id || ''}
                            disabled={busy || !c.canEdit || !presets || !presets.canEdit}
                            onChange={(e) => {
                                const id = e.target.value || null;
                                const name = presets?.items.find((x) => x.id === id)?.name;
                                setLastAction('preset');
                                c.setMemberPreset(user, presetOrgId, id).then((ok) => {
                                    if (ok) onToast(`${user.display_name} starts from ${name || 'the system default'}`);
                                });
                            }}
                        >
                            <option value="">None (system default)</option>
                            {(presets?.items || []).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                            {user.preset_id && presets && !presets.items.some((x) => x.id === user.preset_id) && (
                                <option value={user.preset_id}>{user.preset_name || 'Current preset'}</option>
                            )}
                        </select>
                        <span className="ad2-hint">
                            {presetInfo.chose
                                ? <>User chose: <b>{presetInfo.chose}</b>. Their own pick wins over this assignment.</>
                                : 'The starting inventory columns. Layout only, never access; they can change their own.'}
                        </span>
                    </label>
                )}
                {home?.type === 'TOG' && (
                    <Note tone="warn">
                        <b>Holding members see no dealership data today.</b> Access walks down from a dealership membership,
                        not up from a holding. Move them to a dealership if they need data.
                    </Note>
                )}
                {home?.type === 'VENDOR' && user.role === 'user' && (
                    <Note tone="warn"><b>Fusable staff with the User role reach no dealership.</b> Only Super Admins get the staff view.</Note>
                )}
            </section>

            <section className="ad2-dr-sec" aria-label="Apps">
                <h3>Apps</h3>
                <div className="ad2-dz-row ad2-dz-row--flat">
                    <div className="ad2-dz-tx"><b>IronSight</b>Turns their dashboard access on or off without removing them.</div>
                    <AppSwitch
                        on={Boolean(user.apps?.pricepath)}
                        label={`IronSight access for ${user.display_name}`}
                        title={gApp || (user.apps?.pricepath ? 'Revoke IronSight access' : 'Grant IronSight access')}
                        disabled={busy || !c.canEdit || Boolean(gApp)}
                        onToggle={() => patch(
                            { apps: user.apps?.pricepath ? [] : ['pricepath'] },
                            `IronSight access ${user.apps?.pricepath ? 'removed for' : 'granted to'} ${user.display_name}`,
                        )}
                    />
                </div>
            </section>

            <section className="ad2-dr-sec" aria-label="Sign-in">
                <h3>Sign-in</h3>
                <table className="ad2-mx">
                    <tbody>
                        <tr>
                            <td className="ad2-why ad2-w40">Sign-in address</td>
                            <td>{addr ? <span className="ad2-slug">{addr}</span> : <span className="ad2-muted">—</span>}</td>
                        </tr>
                    </tbody>
                </table>
                <div className="ad2-row">
                    <Button disabled={busy || Boolean(gReset)} title={gReset || undefined} onClick={() => c.setResetConfirm(user.id)}>
                        <KeyIcon />Reset password
                    </Button>
                    <span className="ad2-muted ad2-small">Emails a temporary password. It is never shown to admins.</span>
                </div>
            </section>

            <section className="ad2-dr-sec" aria-label="Account">
                <h3>Account</h3>
                <div className="ad2-danger">
                    <div className="ad2-dz-row">
                        <div className="ad2-dz-tx">
                            <b>{user.status === 'active' ? 'Deactivate' : 'Reactivate'}</b>
                            {gStatus || (user.status === 'active'
                                ? 'Blocks sign-in, keeps their record and access for later.'
                                : 'Lets them sign in again with the same access.')}
                        </div>
                        <Button
                            className={user.status === 'active' ? 'ad2-btn-danger' : undefined}
                            disabled={busy || Boolean(gStatus)}
                            onClick={() => patch(
                                { status: user.status === 'active' ? 'suspended' : 'active' },
                                `${user.display_name} ${user.status === 'active' ? 'deactivated' : 'reactivated'}`,
                            )}
                        >
                            {user.status === 'active' ? 'Deactivate' : 'Reactivate'}
                        </Button>
                    </div>
                    <div className="ad2-dz-row">
                        {confirming ? (
                            <div className="ad2-inline-confirm">
                                <p>
                                    Remove <b>{user.display_name}</b> from IronSight? Their directory sign-in stays;
                                    their IronSight record and access are deleted.
                                </p>
                                <Button onClick={() => c.setConfirmId(null)}>Cancel</Button>
                                <Button
                                    ref={removeYesRef}
                                    variant="danger"
                                    disabled={busy}
                                    onClick={(e) => {
                                        // The 400 ms / double-click guard of every v2 confirm.
                                        if (e.detail > 1 || Date.now() - confirmedAt.current < CONFIRM_GUARD_MS) return;
                                        const name = user.display_name;
                                        c.doRemove(user).then((ok) => { if (ok) { onToast(`${name} removed from IronSight`); onClose(); } });
                                    }}
                                >
                                    Remove
                                </Button>
                            </div>
                        ) : (
                            <>
                                <div className="ad2-dz-tx">
                                    <b>Remove from IronSight</b>
                                    {gRemove || 'Deletes their IronSight record. Their directory sign-in is untouched.'}
                                </div>
                                <Button className="ad2-btn-danger" disabled={Boolean(gRemove)} onClick={() => c.setConfirmId(user.id)}>Remove</Button>
                            </>
                        )}
                    </div>
                </div>
            </section>
            {lastAction && <span className="v2-sr" aria-live="polite">{busy ? 'Saving…' : ''}</span>}
        </Drawer>
    );
}

UserDrawer.propTypes = {
    user: PropTypes.object.isRequired,
    c: PropTypes.object.isRequired,
    me: PropTypes.object,
    tenants: PropTypes.array,
    summary: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    onToast: PropTypes.func.isRequired,
};

export default UserDrawer;
